// ═══════════════════════════════════════════════════════════
// WebMCP Integration (Browser Model Context Protocol)
// Exposes client-side site tools to AI agents via navigator.modelContext
// Specification: https://webmachinelearning.github.io/webmcp/
// ═══════════════════════════════════════════════════════════

export interface WebMCPTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (
    args: Record<string, unknown>,
  ) => Promise<Record<string, unknown>> | Record<string, unknown>;
}

declare global {
  interface Navigator {
    modelContext?: {
      provideContext?: (config: { tools: WebMCPTool[] }) => void;
    };
  }
}

/**
 * Initialize WebMCP context provider if supported by the agent browser.
 */
export function initWebMCP(): boolean {
  if (typeof window === "undefined" || !navigator.modelContext?.provideContext) {
    return false;
  }

  try {
    navigator.modelContext.provideContext({
      tools: [
        {
          name: "calculate_commission",
          description:
            "Calculate expected sales commission based on deal amount and percentage rate.",
          inputSchema: {
            type: "object",
            properties: {
              dealAmount: { type: "number", description: "Monetary amount of the deal" },
              ratePercentage: {
                type: "number",
                description: "Commission percentage (e.g. 10 for 10%)",
              },
            },
            required: ["dealAmount", "ratePercentage"],
          },
          execute: (args) => {
            const dealAmount = Number(args["dealAmount"]) || 0;
            const ratePercentage = Number(args["ratePercentage"]) || 0;
            const commission = (dealAmount * ratePercentage) / 100;
            return {
              ok: true,
              dealAmount,
              ratePercentage,
              commission,
              currency: "USD",
            };
          },
        },
        {
          name: "simulate_rule_calculation",
          description:
            "Simulate a commission rule calculation including caps, floors, and margin deductions.",
          inputSchema: {
            type: "object",
            properties: {
              dealAmount: { type: "number", description: "Gross deal revenue" },
              cogs: { type: "number", description: "Cost of Goods Sold (for margin basis)" },
              rate: { type: "number", description: "Commission rate percentage" },
              cap: { type: "number", description: "Maximum ceiling payout (optional)" },
              floor: { type: "number", description: "Minimum floor payout (optional)" },
            },
            required: ["dealAmount", "rate"],
          },
          execute: (args) => {
            const dealAmount = Number(args["dealAmount"]) || 0;
            const cogs = Number(args["cogs"]) || 0;
            const rate = Number(args["rate"]) || 0;
            const cap = args["cap"] != null ? Number(args["cap"]) : null;
            const floor = args["floor"] != null ? Number(args["floor"]) : null;

            const margin = Math.max(0, dealAmount - cogs);
            let rawCommission = (margin * rate) / 100;
            let capApplied = false;
            let floorApplied = false;

            if (cap !== null && rawCommission > cap) {
              rawCommission = cap;
              capApplied = true;
            }
            if (floor !== null && rawCommission < floor) {
              rawCommission = floor;
              floorApplied = true;
            }

            return {
              ok: true,
              dealAmount,
              cogs,
              basisAmount: margin,
              rawCommission,
              finalCommission: rawCommission,
              capApplied,
              floorApplied,
            };
          },
        },
        {
          name: "get_rule_structures",
          description: "List supported commission plan structures available in BitCommissions24.",
          inputSchema: {
            type: "object",
            properties: {},
          },
          execute: () => {
            return {
              ok: true,
              structures: [
                { type: "STRAIGHT", name: "Straight Percentage", plan: "All Tiers" },
                { type: "SPLIT", name: "Multi-Rep Team Split", plan: "Enterprise" },
                { type: "SPIFF", name: "Time-Limited Bonus Campaign", plan: "Enterprise" },
                {
                  type: "BASE_PLUS",
                  name: "Base Salary + Variable Commission",
                  plan: "Enterprise",
                },
                { type: "TIERED", name: "Progressive Threshold Tiers", plan: "Enterprise" },
              ],
            };
          },
        },
      ],
    });
    return true;
  } catch (err) {
    console.warn("[WebMCP] Initialization warning:", err);
    return false;
  }
}
